import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, createUser, deleteUser, downloadText, exportEntries, getUserCount, getUsers, updateUser, type User, type UserRole } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";

export function useUserManagementViewModel() {
  const { user } = useAuth();
  const { isAdmin } = useDashboardRoles();
  const queryClient = useQueryClient();
  const [usersPage, setUsersPage] = React.useState(0);
  const [usersPageSize, setUsersPageSize] = React.useState(25);
  const [usersSearch, setUsersSearch] = React.useState("");
  const [newUser, setNewUser] = React.useState({
    email: "",
    password: "",
    full_name: "",
    company_name: user?.company_name ?? "Synergy Fiber",
    role: "employee" as Exclude<UserRole, "admin" | "manager" | "readonly_admin">,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState<User | null>(null);
  const [deleteExported, setDeleteExported] = React.useState(false);

  const { data: usersData } = useQuery({
    queryKey: ["users", usersSearch, usersPage, usersPageSize],
    queryFn: () => getUsers({ include_inactive: true, search: usersSearch.trim() || undefined, limit: usersPageSize, offset: usersPage * usersPageSize }),
    enabled: isAdmin,
  });
  const { data: usersCountData } = useQuery({
    queryKey: ["users-count", usersSearch],
    queryFn: () => getUserCount({ include_inactive: true, search: usersSearch.trim() || undefined }),
    enabled: isAdmin,
  });
  const createUserMutation = useMutation({
    mutationFn: () => createUser(newUser),
    onSuccess: async () => {
      setNewUser((current) => ({ ...current, email: "", password: "", full_name: "" }));
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
  const toggleUserMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) => updateUser(id, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
  const deleteUserMutation = useMutation({
    mutationFn: (id: number) => deleteUser(id),
    onSuccess: async () => {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setDeleteExported(false);
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  return {
    user, isAdmin,
    usersPage, setUsersPage, usersPageSize, setUsersPageSize, usersSearch, setUsersSearch,
    newUser, setNewUser,
    usersData, usersCountData,
    createUserMutation, toggleUserMutation, deleteUserMutation,
    deleteDialogOpen, setDeleteDialogOpen, userToDelete, setUserToDelete, deleteExported, setDeleteExported,
    downloadText, exportEntries, ApiError,
  };
}
